import React from 'react';
import cn from '@/utils/cn';
import type { Interval, LaneState, TimeRange } from './deriveParticipantStates';
import { formatSeconds } from './timeFormat';

interface StateBarProps {
  end: number;
  intervals: Interval[];
  label: string;
  start: number;
  teacherDrops: TimeRange[];
}

export const LANE_LABEL: Record<LaneState, string> = {
  disconnected: 'Locked, socket down',
  following: 'Locked, following',
  free: 'On page, unlocked',
  off_page: 'Not on the page',
};

const LANE_CLASS: Record<LaneState, string> = {
  disconnected: 'tw-bg-red-500',
  following: 'tw-bg-green-500',
  free: 'tw-bg-yellow-400',
  off_page: 'tw-bg-gray-200',
};

const pct = (value: number, start: number, end: number) => {
  const span = Math.max(1, end - start);
  return `${Math.min(100, Math.max(0, ((value - start) / span) * 100))}%`;
};

const summarize = (intervals: Interval[]): string => {
  const totals = new Map<LaneState, number>();
  intervals.forEach(i => totals.set(i.state, (totals.get(i.state) ?? 0) + (i.end - i.start)));
  return Array.from(totals.entries())
    .filter(([state]) => state !== 'off_page')
    .map(([state, ms]) => `${LANE_LABEL[state]} ${formatSeconds(ms / 1000)}`)
    .join(', ') || 'never on the page';
};

// One horizontal bar per student: colored by derived state, with the teacher's drops shown as
// hatched bands so a "free" stretch can be read against what the teacher was doing.
const StateBar = ({ end, intervals, label, start, teacherDrops }: StateBarProps) => (
  <div
    aria-label={`${label}: ${summarize(intervals)}`}
    className="tw-relative tw-h-3 tw-w-full tw-rounded tw-overflow-hidden tw-bg-gray-100"
    role="img"
  >
    {intervals.map(interval => (
      <div
        key={`${interval.start}-${interval.state}`}
        className={cn('tw-absolute tw-inset-y-0', LANE_CLASS[interval.state])}
        style={{ left: pct(interval.start, start, end), width: pct(interval.end, start, end) }}
        title={`${LANE_LABEL[interval.state]} · ${formatSeconds((interval.end - interval.start) / 1000)}`}
      />
    ))}
    {teacherDrops.map(drop => (
      <div
        key={drop.start}
        aria-hidden
        className="tw-absolute tw-inset-y-0 tw-border-x tw-border-solid tw-border-red-700 tw-bg-red-700/30"
        style={{ left: pct(drop.start, start, end), width: pct(drop.end, start, end) }}
        title={`Teacher dropped · ${formatSeconds((drop.end - drop.start) / 1000)}`}
      />
    ))}
  </div>
);

export default StateBar;
